// frontend/src/pages/FavoritesManagerPage.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, Typography, Button, Paper, CircularProgress, Alert, Accordion, AccordionSummary, AccordionDetails, 
  IconButton, Tooltip, Chip, Stack 
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import favoritesService from '../services/favoritesService';
import FavoriteGroupModal from '../components/favorites/FavoriteGroupModal'; 
import { useNotification } from '../context/NotificationContext';

const FavoritesManagerPage = () => {
  const { showSuccess, showError } = useNotification();
  const [favorites, setFavorites] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // States para o modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);

  const fetchFavorites = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await favoritesService.getAll();
      setFavorites(response.data);
    } catch (err) {
      setError('Falha ao carregar os grupos favoritos.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites]);

  const handleOpenCreateModal = () => {
    setEditingGroup(null);
    setIsModalOpen(true);
  };
  
  const handleOpenEditModal = (group) => {
    setEditingGroup(group);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este grupo?')) {
      try {
        await favoritesService.delete(id);
        fetchFavorites(); // Recarrega a lista
        showSuccess('Grupo favorito excluído com sucesso!');
      } catch (err) {
        showError('Falha ao excluir o grupo.');
      }
    }
  };

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Gerenciar Grupos Favoritos</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreateModal}>
          Criar Novo Grupo
        </Button>
      </Box>
      
      {favorites.length === 0 ? (
        <Typography>Você ainda não criou nenhum grupo. Clique em "Criar Novo Grupo" para começar.</Typography>
      ) : (
        <Box>
          {favorites.map(group => (
            <Accordion key={group.id}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <Typography sx={{ fontWeight: 500 }}>{group.nome}</Typography>
                  <Box>
                    <Tooltip title="Editar Grupo">
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenEditModal(group); }}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir Grupo">
                      <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDelete(group.id); }}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="subtitle2" gutterBottom>Assinantes no grupo:</Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {group.assinantes.map(signer => (
                    <Chip key={signer.id} label={signer.nome_completo} />
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {isModalOpen && (
        <FavoriteGroupModal
            open={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onSave={fetchFavorites}
            group={editingGroup}
        />
    )}
    </Paper>
  );
};

export default FavoritesManagerPage;